import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { WithAccountId } from '@mews-commander/types';
import { SuspenseBoundary } from '@mews-commander/ui';
import { useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { useLocalizeText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { PaymentPlanPaymentDetailsAccordion } from '../PaymentPlanPaymentDetailsAccordion';
import { CreatePaymentPlanFormValues } from '../schema';
import { CreatePaymentPlanFormField, ReservationForCreatePaymentPlan } from '../types';
import { CreatePaymentPlanDialogIntervalFields } from './CreatePaymentPlanDialogIntervalFields';
import { CreatePaymentPlanPaymentMethodsField } from './CreatePaymentPlanPaymentMethodsField';
import { CreatePaymentPlanBillingModeField } from './CreatePaymentPlanBillingModeField';

export const PlanDetailsReservationFields = ({
    reservation,
    accountId,
}: {
    reservation: ReservationForCreatePaymentPlan;
} & WithAccountId) => {
    const localizeText = useLocalizeText();
    const { setError, clearErrors, getFieldState } = useFormContext<CreatePaymentPlanFormValues>();
    const {
        data: { paymentDueDatesAndAmounts },
    } = useRepositorySuspenseQuery('paymentPlanCreateForm', 'get', {
        reservationId: reservation.reservationId,
    });

    const hasInsufficientAmounts = paymentDueDatesAndAmounts && paymentDueDatesAndAmounts.length < 2;
    const isReservationValid = !hasInsufficientAmounts && reservation.isValid;
    const { invalid: hasReservationError } = getFieldState(CreatePaymentPlanFormField.ReservationId);

    useEffect(() => {
        if (hasInsufficientAmounts) {
            setError(CreatePaymentPlanFormField.ReservationId, {
                message: localizeText({
                    textKey: TextKey.CreatePaymentPlanFormReservationInsufficientAmountsErrorMessage,
                }),
            });
        } else if (isReservationValid) {
            clearErrors(CreatePaymentPlanFormField.ReservationId);
        }
    }, [hasInsufficientAmounts, isReservationValid, hasReservationError, setError, clearErrors, localizeText]);

    return isReservationValid ? (
        <>
            <CreatePaymentPlanDialogIntervalFields reservation={reservation} />
            <SuspenseBoundary loader="inline">
                <PaymentPlanPaymentDetailsAccordion accountId={accountId} reservation={reservation} />
            </SuspenseBoundary>
            <SuspenseBoundary loader="inline">
                <CreatePaymentPlanPaymentMethodsField reservationId={reservation.reservationId} />
            </SuspenseBoundary>
            <CreatePaymentPlanBillingModeField />
        </>
    ) : (
        <CreatePaymentPlanBillingModeField />
    );
};
